import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { getProposedPageUpdate } from '../components/PageUpdateApprovalCard';
import { FORM_UI_REASONS } from './constants';
import type { FormUiFieldIssue, FormUiFields } from './types';
import { useUiContext } from './uiContext';
import {
  describeFormUiField,
  findFormUiField,
  flattenFormErrors,
  getBlockedFormUiFieldReason,
  getFormUiFieldEntries,
  getFormUiReadDescription,
  getFormUiWriteDescription,
  getInvalidOptionReason,
  isFormUiFieldName,
  isFormUiFieldVisible,
  isMissingRequiredValue,
  previewFormUiUpdate,
} from './utils';

type Props<TForm extends FieldValues> = {
  name: string;
  prompt: string;
  methods: UseFormReturn<TForm>;
  isEnabled: boolean;
  fields: FormUiFields<TForm>;
  getState?: () => Record<string, unknown>;
  getUnavailableReason?: () => string | undefined;
};

const useFormUiContext = <TForm extends FieldValues>({
  name,
  prompt,
  methods,
  isEnabled,
  fields,
  getState,
  getUnavailableReason,
}: Props<TForm>) => {
  const readForm = () => {
    const values = methods.getValues();
    const visibleFields = getFormUiFieldEntries(fields)
      .filter(([, field]) => isFormUiFieldVisible(field, values))
      .map(([fieldName, field]) => ({
        fieldName,
        field,
        value: methods.getValues(fieldName),
        lockedReason: field.lockedReason?.(values),
      }));
    return {
      form: Object.fromEntries(
        visibleFields
          .filter(({ field }) => !field.secret)
          .map(({ fieldName, value }) => [fieldName, value ?? null]),
      ),
      fields: visibleFields.map(({ fieldName, field, value, lockedReason }) =>
        describeFormUiField(fieldName, field, value, lockedReason),
      ),
      missingRequiredFields: visibleFields
        .filter(
          ({ field, value, lockedReason }) =>
            !lockedReason && isMissingRequiredValue(field, value),
        )
        .map(({ fieldName }) => fieldName),
      validationErrors: visibleFields.reduce<Record<string, string>>(
        (errors, { fieldName }) => ({
          ...errors,
          ...flattenFormErrors(
            methods.getFieldState(fieldName).error,
            fieldName,
          ),
        }),
        {},
      ),
      ...getState?.(),
    };
  };

  const rejectWrite = (reason: string) => ({
    applied: [],
    rejected: [{ field: 'form', reason }],
    ...readForm(),
  });

  useUiContext(
    isEnabled
      ? {
          [`read_${name}`]: {
            description: getFormUiReadDescription(name, prompt),
            handler: () => {
              const unavailableReason = getUnavailableReason?.();
              return unavailableReason
                ? { unavailable: unavailableReason }
                : readForm();
            },
          },
          [`write_${name}`]: {
            description: getFormUiWriteDescription(name, prompt),
            requiresApproval: true,
            enabledModes: ['agent'],
            preview: (proposed) => previewFormUiUpdate(fields, proposed),
            argsSchema: {
              type: 'object',
              properties: {
                form: { type: ['object', 'string'] },
                reason: { type: 'string' },
              },
            },
            handler: async (args) => {
              const unavailableReason = getUnavailableReason?.();
              if (unavailableReason) {
                return rejectWrite(unavailableReason);
              }
              const proposed = getProposedPageUpdate(args);
              if (!proposed) {
                return rejectWrite(FORM_UI_REASONS.invalidForm);
              }

              const rejected: FormUiFieldIssue[] = [];
              const pending = new Map<string, unknown>();
              Object.entries(proposed).forEach(([fieldName, value]) => {
                const field = findFormUiField(fields, fieldName);
                if (!field) {
                  rejected.push({
                    field: fieldName,
                    reason: FORM_UI_REASONS.unknownField,
                  });
                  return;
                }
                if (field.secret) {
                  rejected.push({
                    field: fieldName,
                    reason: FORM_UI_REASONS.userOnlyField,
                  });
                  return;
                }
                const invalidOptionReason = getInvalidOptionReason(
                  field.options,
                  value,
                );
                if (invalidOptionReason) {
                  rejected.push({
                    field: fieldName,
                    reason: invalidOptionReason,
                  });
                  return;
                }
                pending.set(fieldName, value);
              });

              const getValuesAfterWrite = () => ({
                ...methods.getValues(),
                ...Object.fromEntries(pending),
              });
              const rejectBlockedFields = () => {
                const values = getValuesAfterWrite();
                const blocked = getFormUiFieldEntries(fields).flatMap(
                  ([fieldName, field]) => {
                    const reason = pending.has(fieldName)
                      ? getBlockedFormUiFieldReason(field, values)
                      : undefined;
                    return reason ? [{ field: fieldName, reason }] : [];
                  },
                );
                blocked.forEach((issue) => {
                  rejected.push(issue);
                  pending.delete(issue.field);
                });
                if (blocked.length > 0) {
                  rejectBlockedFields();
                }
              };
              rejectBlockedFields();

              const applied: Path<TForm>[] = [];
              const changedByHandlers = new Set<Path<TForm>>();
              const handlerMethods: UseFormReturn<TForm> = {
                ...methods,
                setValue: (name, value, options) => {
                  changedByHandlers.add(name);
                  methods.setValue(name, value, options);
                },
              };
              getFormUiFieldEntries(fields).forEach(([fieldName, field]) => {
                if (!pending.has(fieldName)) {
                  return;
                }
                const value = pending.get(fieldName);
                const blockedReason = getBlockedFormUiFieldReason(
                  field,
                  methods.getValues(),
                );
                pending.delete(fieldName);
                if (blockedReason) {
                  rejected.push({ field: fieldName, reason: blockedReason });
                  return;
                }
                if (field.onWrite) {
                  const result = field.onWrite(value, handlerMethods, proposed);
                  if (result.ok) {
                    applied.push(fieldName);
                  } else {
                    rejected.push({ field: fieldName, reason: result.reason });
                  }
                  return;
                }
                methods.setValue(
                  fieldName,
                  value as PathValue<TForm, Path<TForm>>,
                  { shouldDirty: true },
                );
                applied.push(fieldName);
              });

              changedByHandlers.forEach((name) => {
                const [topLevelName] = name.split('.');
                if (
                  isFormUiFieldName(fields, topLevelName) &&
                  !applied.includes(topLevelName)
                ) {
                  applied.push(topLevelName);
                }
              });
              const changed = [...new Set([...applied, ...changedByHandlers])];
              if (changed.length > 0) {
                await methods.trigger(changed);
              }
              return { applied, rejected, ...readForm() };
            },
          },
        }
      : {},
  );
};

export default useFormUiContext;
